/**
 * 개별 컴포넌트 아이템 (드래그 가능)
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  GripVertical,
  Play,
  ClipboardList,
  FileText,
  BookOpen,
  MousePointer2,
  MessageSquare,
  Download,
  ChevronDown,
  ChevronUp,
  Settings,
  Lock
} from 'lucide-react'
import type { ComponentConfig, ComponentType, SequenceOverrides } from '@/types/configuration'
import { COMPONENT_TYPE_LABELS, QUIZ_LOCATION_LABELS } from '@/types/configuration'
import { ComponentOptionsEditor } from './ComponentOptionsEditor'

interface ComponentItemProps {
  component: ComponentConfig
  index: number
  onUpdate: (index: number, component: ComponentConfig) => void
  onMoveUp: (index: number) => void
  onMoveDown: (index: number) => void
  onDragStart: (e: React.DragEvent, index: number) => void
  onDragOver: (e: React.DragEvent, index: number) => void
  onDragEnd: () => void
  isFirst: boolean
  isLast: boolean
  isDragging: boolean
  dragOverIndex: number | null
  sequenceOverrides?: SequenceOverrides
}

// 컴포넌트 타입별 아이콘
const ComponentIcon: React.FC<{ type: ComponentType; className?: string }> = ({ type, className = 'w-5 h-5' }) => {
  switch (type) {
    case 'video':
      return <Play className={className} />
    case 'quiz':
      return <ClipboardList className={className} />
    case 'assignment':
      return <FileText className={className} />
    case 'reading':
      return <BookOpen className={className} />
    case 'interactive':
      return <MousePointer2 className={className} />
    case 'discussion':
      return <MessageSquare className={className} />
    case 'download':
      return <Download className={className} />
    default:
      return <FileText className={className} />
  }
}

export const ComponentItem: React.FC<ComponentItemProps> = ({
  component,
  index,
  onUpdate,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragOver,
  onDragEnd,
  isFirst,
  isLast,
  isDragging,
  dragOverIndex,
  sequenceOverrides
}) => {
  const { t } = useTranslation('configuration')
  const [isExpanded, setIsExpanded] = useState(false)

  const isOrderLocked = sequenceOverrides?.orderOverridable === false
  const isOptionsLocked = sequenceOverrides?.optionsOverridable === false
  // companyEditable === false → 보이지만 토글/옵션 변경 불가
  const isComponentLocked = component.companyEditable === false

  const handleToggleEnabled = () => {
    if (isComponentLocked) return
    onUpdate(index, { ...component, enabled: !component.enabled })
  }

  const handleToggleRequired = () => {
    if (isComponentLocked || !component.enabled) return
    onUpdate(index, { ...component, required: !component.required })
  }

  const getComponentLabel = (): string => {
    const typeLabel = COMPONENT_TYPE_LABELS[component.type]
    if (component.type === 'quiz' && component.location) {
      return `${typeLabel} (${QUIZ_LOCATION_LABELS[component.location]})`
    }
    return typeLabel
  }

  const isDragOver = dragOverIndex === index

  return (
    <div
      draggable={!isOrderLocked}
      onDragStart={(e) => !isOrderLocked && onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragEnd={onDragEnd}
      className={`
        border rounded-lg transition-all duration-200
        ${component.enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'}
        ${isDragging ? 'opacity-50' : ''}
        ${isDragOver ? 'border-blue-500 border-2 shadow-lg' : ''}
      `}
    >
      {/* 메인 행 */}
      <div className="flex items-center gap-3 p-3">
        {/* 드래그 핸들 */}
        <div className={isOrderLocked ? "text-gray-300" : "cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"}>
          <GripVertical className="w-5 h-5" />
        </div>

        {/* 순서 번호 */}
        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">
          {index + 1}
        </div>

        {/* 아이콘 */}
        <div className={`p-2 rounded-lg ${component.enabled ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
          <ComponentIcon type={component.type} />
        </div>

        {/* 라벨 */}
        <div className="flex-1">
          <div className="font-medium text-gray-900">{getComponentLabel()}</div>
          <div className="text-sm text-gray-500">
            {isComponentLocked
              ? (component.enabled ? (component.required ? t('componentItem.requiredFixed') : t('componentItem.optionalFixed')) : t('componentItem.disabledFixed'))
              : component.enabled
                ? <span
                    onClick={handleToggleRequired}
                    className={`cursor-pointer inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${
                      component.required
                        ? 'border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100'
                        : 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                    }`}
                  >{component.required ? t('componentItem.required') : t('componentItem.optional')}</span>
                : t('componentItem.disabled')
            }
          </div>
        </div>

        {/* 토글 버튼들 */}
        <div className="flex items-center gap-2">
          {/* 고정 표시 */}
          {isComponentLocked && (
            <span title={t('componentItem.lockedByParent')}>
              <Lock className="w-4 h-4 text-gray-400" />
            </span>
          )}

          {/* 활성화 토글 */}
          <button
            onClick={handleToggleEnabled}
            disabled={isComponentLocked}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
              ${isComponentLocked ? 'cursor-not-allowed opacity-50' : ''}
              ${component.enabled ? 'bg-blue-600' : 'bg-gray-200'}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                ${component.enabled ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>

          {/* 순서 변경 버튼 */}
          <div className="flex flex-col">
            <button
              onClick={() => onMoveUp(index)}
              disabled={isFirst || isOrderLocked}
              className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => onMoveDown(index)}
              disabled={isLast || isOrderLocked}
              className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* 설정 버튼 */}
          <button
            onClick={() => !isOptionsLocked && !isComponentLocked && setIsExpanded(!isExpanded)}
            disabled={isOptionsLocked || isComponentLocked}
            className={`
              p-1.5 rounded-md transition-colors
              ${(isOptionsLocked || isComponentLocked) ? 'text-gray-300 cursor-not-allowed' : isExpanded ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}
            `}
          >
            {(isOptionsLocked || isComponentLocked) ? <Lock className="w-4 h-4" /> : <Settings className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* 옵션 패널 (확장 시) */}
      {isExpanded && !isComponentLocked && component.enabled && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          <ComponentOptionsEditor
            component={component}
            onUpdate={(updated) => onUpdate(index, updated)}
            readOnly={isOptionsLocked}
          />
        </div>
      )}
    </div>
  )
}
